/**
 * StaggeredList — animates its children with a sequenced entrance.
 *
 * Wraps a list of arbitrary children and reveals them with a soft
 * fade-and-rise, offset by a per-item delay. Used for theorem-stack
 * reveals, evidence-row drops, and any "n items arriving" surface
 * where a single en-masse appearance feels jarring.
 *
 * The component keeps the children static after the entrance plays —
 * it does not replay on subsequent renders unless `triggerKey`
 * changes. Honors `prefers-reduced-motion`: the children render
 * immediately with no offset under reduced motion.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { motion, useReducedMotion, type Variants } from "motion/react";

type MotionDivStyle = NonNullable<React.ComponentProps<typeof motion.div>["style"]>;

export interface StaggeredListProps {
  /** List items to animate. */
  readonly children: React.ReactNode;
  /** Delay between sibling animations (ms). Defaults to 70. */
  readonly staggerMs?: number;
  /** Initial offset (px) along the Y axis. Defaults to 10. */
  readonly distance?: number;
  /** Per-item animation duration (ms). Defaults to 320. */
  readonly durationMs?: number;
  /**
   * Identity of the current list. Changing this replays the entrance
   * (useful when the list contents are swapped wholesale).
   */
  readonly triggerKey?: string | number;
  /** Optional className applied to the outer wrapper. */
  readonly className?: string;
  /** Optional inline style applied to the outer wrapper. */
  readonly style?: React.CSSProperties;
}

function buildContainerVariants(staggerMs: number): Variants {
  return {
    hidden: {},
    visible: {
      transition: { staggerChildren: staggerMs / 1000 },
    },
  };
}

function buildItemVariants(distance: number, durationMs: number): Variants {
  return {
    hidden: { opacity: 0, y: distance },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: durationMs / 1000, ease: "easeOut" },
    },
  };
}

/** Wraps its children in a staggered fade + rise entrance. */
export const StaggeredList = React.memo(function StaggeredList({
  children,
  staggerMs = 70,
  distance = 10,
  durationMs = 320,
  triggerKey,
  className,
  style,
}: StaggeredListProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const containerVariants = React.useMemo(() => buildContainerVariants(staggerMs), [staggerMs]);
  const itemVariants = React.useMemo(
    () => buildItemVariants(distance, durationMs),
    [distance, durationMs],
  );

  if (prefersReducedMotion) {
    return (
      <div className={className} style={style} data-csk-staggered-list="reduced-motion">
        {children}
      </div>
    );
  }

  const childArray = React.Children.toArray(children);
  const renderedChildren = childArray.map((child, index) => (
    <motion.div key={index} variants={itemVariants} data-csk-staggered-item={index}>
      {child}
    </motion.div>
  ));

  if (style !== undefined) {
    const motionStyle = style as MotionDivStyle;
    return (
      <motion.div
        key={triggerKey ?? "staggered-list"}
        className={className}
        style={motionStyle}
        data-csk-staggered-list=""
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {renderedChildren}
      </motion.div>
    );
  }

  return (
    <motion.div
      key={triggerKey ?? "staggered-list"}
      className={className}
      data-csk-staggered-list=""
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {renderedChildren}
    </motion.div>
  );
});
